import {
  getRegionalUpdaterGate,
  type RegionalUpdaterGate,
} from '@/components/constants/flags';
import { getCachedKindeRegion } from '@/hooks/query/useQueryKindeProperties';
import { type Region } from '@heidi/schema/ml-scribe';
import { check } from '@tauri-apps/plugin-updater';
import { updaterBridge } from './index';

/**
 * The resolved updater feed for the current gate + region. This is the single
 * source of truth for the compliance-critical routing decision (APP-6113 §5.19,
 * DC3) — every consumer (the optional-updater model, the settings debug page,
 * the popover trigger) routes through {@link selectUpdaterFeed} so the
 * fail-closed ladder can't drift between copies.
 *
 * - `blocked`  — fail-closed: the flag provider hasn't resolved, or the gate is
 *   ON but the authenticated region is unresolved. No update is offered; never
 *   fall through to a default/`stable` channel (a single global feed could serve
 *   UK/EU an uncertified version).
 * - `global`   — gate OFF: today's global updater (no region isolation).
 * - `regional` — gate ON + region resolved: bind the CrabNebula channel to the
 *   region in Rust.
 */
export type UpdaterFeed =
  | { kind: 'blocked' }
  | { kind: 'global' }
  | { kind: 'regional'; region: Region };

/**
 * The single fail-closed decision. Pure — takes the gate and region as inputs so
 * both the non-React path ({@link resolveUpdaterFeed}) and React consumers
 * (settings / popover, which read them via hooks) share the exact same logic.
 *
 * `region` MUST come from `kindeProperties.region` — never `getRegion()` /
 * `useRegion()`, which fail open to AU and honour `?region=` (C10).
 */
export function selectUpdaterFeed(
  gate: RegionalUpdaterGate,
  region: Region | undefined
): UpdaterFeed {
  if (gate === 'unknown') return { kind: 'blocked' }; // DC3: provider not ready
  if (gate === 'off') return { kind: 'global' };
  if (!region) return { kind: 'blocked' }; // DC3: ON but region unresolved
  return { kind: 'regional', region };
}

/**
 * Non-React feed resolution (used by the rematch model). Reads the static gate
 * and the cached region. Returns `{ kind: 'blocked' }` until both resolve.
 */
export function resolveUpdaterFeed(): UpdaterFeed {
  return selectUpdaterFeed(getRegionalUpdaterGate(), getCachedKindeRegion());
}

/**
 * Execute a check for the resolved feed. `blocked` resolves to `null` (no
 * update) defensively so a mis-routed caller still fails closed. Both feeds
 * normalise to `{ version, body }` so downstream wiring is identical.
 *
 * Deliberately imposes no deadline of its own. Both updater calls are bounded
 * inside the Rust updater, which is the layer that owns the connection and the
 * bundle: a timeout there genuinely cancels the transfer, whereas one here
 * could only stop us waiting while the download continued and still replaced
 * the app. A frontend deadline would also be a new failure branch reaching
 * every desktop user on the global path, tunable only by shipping a build.
 */
export async function checkUpdateForFeed(
  feed: UpdaterFeed
): Promise<{ version: string; body?: string } | null> {
  if (feed.kind === 'blocked') return null;
  if (feed.kind === 'regional') {
    return updaterBridge.checkUpdateForRegion(feed.region);
  }
  const update = await check(); // OFF: global updater
  return update ? { version: update.version, body: update.body } : null;
}

/**
 * Install the update for the resolved feed. Resolves `true` when an update was
 * installed (the caller relaunches). `blocked` resolves to `false`.
 */
export async function installUpdateForFeed(feed: UpdaterFeed): Promise<boolean> {
  if (feed.kind === 'blocked') return false;
  if (feed.kind === 'regional') {
    return updaterBridge.installUpdateForRegion(feed.region);
  }
  const update = await check(); // OFF: global updater
  if (!update) return false;
  await update.downloadAndInstall();
  return true;
}
